import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  selectCrossClientLandingDecompositionResult,
  setCrossClientLandingDecompositionResult,
  setCrossClientEvaluateLoading,
  setCrossClientInitLoading,
} from '../../../store/crossClient/crossClientLanding'
import {
  useDecomposeCrossClientQueryMutation,
  useEvaluateClauseResolversCrossClientQueryMutation,
  useEvaluateCrossClientQueryMutation,
} from '../../../store/crossClient/crossClientExecution'
import { useDeleteCrossClientQueryExecutionMutation } from '../../../store/crossClient/crossClient'
import { selectAccountId } from '../../../store/auth/authSlice'
import {
  type CrossClientQueryAtomicClause,
  type CrossClientQueryClauseResolverMapping,
  type CrossClientQueryClientQueryMode,
  type CrossClientQueryDataSource,
  type CrossClientQueryLogicalQuestionDecomposition,
  type CrossClientQueryLogicalQuestionNNFDecomposition,
  type CrossClientQueryResultSummary,
} from '../../../models/CrossClient'

interface DecomposeArgs {
  userQuestion: string
  sources: CrossClientQueryDataSource[]
  clientQueryMode: CrossClientQueryClientQueryMode
  decomposition?: CrossClientQueryLogicalQuestionDecomposition
  nnfDecomposition?: CrossClientQueryLogicalQuestionNNFDecomposition
  clauseResolverMapping?: CrossClientQueryClauseResolverMapping
}

interface DecomposeOptions {
  skipReviewPipeline?: boolean
}

export type CrossClientReviewState =
  | { kind: 'deep-only'; atomicClauses: CrossClientQueryAtomicClause[] }
  | { kind: 'instant-running'; atomicClauses: CrossClientQueryAtomicClause[] }
  | { kind: 'instant-matches'; atomicClauses: CrossClientQueryAtomicClause[]; resultSummary: CrossClientQueryResultSummary }
  | { kind: 'instant-no-matches'; atomicClauses: CrossClientQueryAtomicClause[]; resultSummary: CrossClientQueryResultSummary }

const INSTANT_MATCH_NAVIGATE_DELAY_MS = 1000

const isFullyEvaluatedByResolvers = (resultSummary: CrossClientQueryResultSummary): boolean =>
  resultSummary.fullCount + resultSummary.noMatchCount === resultSummary.totalCount

export const useCrossClientQueryLandingExecution = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const decompositionResult = useAppSelector(selectCrossClientLandingDecompositionResult)

  const [triggerDecomposeCrossClientQuery] = useDecomposeCrossClientQueryMutation()
  const [triggerEvaluateClauseResolvers] = useEvaluateClauseResolversCrossClientQueryMutation()
  const [triggerEvaluateCrossClientQuery] = useEvaluateCrossClientQueryMutation()
  const [triggerDeleteCrossClientExecution] = useDeleteCrossClientQueryExecutionMutation()

  const [reviewState, setReviewState] = useState<CrossClientReviewState | null>(null)

  const finalizeExecution = useCallback(
    async (executionId: string, resultSummary?: CrossClientQueryResultSummary) => {
      if (resultSummary && isFullyEvaluatedByResolvers(resultSummary)) {
        navigate(`/cross-client/executions/${executionId}`, { replace: true })
        return
      }
      try {
        dispatch(setCrossClientEvaluateLoading(true))
        await triggerEvaluateCrossClientQuery({ accountId, executionId }).unwrap()
        navigate(`/cross-client/executions/${executionId}`, { replace: true })
      } catch (e) {
        console.error(e)
        dispatch(setCrossClientEvaluateLoading(false))
      }
    },
    [dispatch, navigate, accountId, triggerEvaluateCrossClientQuery],
  )

  const decomposeCrossClientQuery = useCallback(
    async (args: DecomposeArgs, options?: DecomposeOptions) => {
      let result
      try {
        dispatch(setCrossClientLandingDecompositionResult(null))
        setReviewState(null)
        dispatch(setCrossClientInitLoading(true))
        result = await triggerDecomposeCrossClientQuery({
          accountId,
          payload: args,
        }).unwrap()
        dispatch(setCrossClientLandingDecompositionResult(result))
      } catch (e) {
        console.error(e)
        return
      } finally {
        dispatch(setCrossClientInitLoading(false))
      }

      if (options?.skipReviewPipeline) {
        return result
      }

      const atomicClauses = result.decomposition?.atomicClauses ?? []
      if (atomicClauses.length === 0) {
        return result
      }
      const hasResolverMappings = result.clauseResolverMapping?.clauseResolverMappings?.some((m) => m.resolverId !== null) ?? false

      if (!hasResolverMappings) {
        setReviewState({ kind: 'deep-only', atomicClauses })
        return result
      }

      setReviewState({ kind: 'instant-running', atomicClauses })
      try {
        const resultSummary = await triggerEvaluateClauseResolvers({ accountId, executionId: result.executionId }).unwrap()
        if (resultSummary.fullCount >= 1) {
          setReviewState({ kind: 'instant-matches', atomicClauses, resultSummary })
          await new Promise((resolve) => setTimeout(resolve, INSTANT_MATCH_NAVIGATE_DELAY_MS))
          await finalizeExecution(result.executionId, resultSummary)
        } else {
          setReviewState({ kind: 'instant-no-matches', atomicClauses, resultSummary })
        }
      } catch (e) {
        console.error(e)
        setReviewState({ kind: 'deep-only', atomicClauses })
      }
      return result
    },
    [dispatch, accountId, triggerDecomposeCrossClientQuery, triggerEvaluateClauseResolvers, finalizeExecution],
  )

  const initError = useMemo(() => {
    if (!decompositionResult) {
      return null
    }
    if (!decompositionResult.decomposition?.atomicClauses?.length) {
      return 'Unable to decompose question'
    }
    return null
  }, [decompositionResult])

  const resetExecution = useCallback(() => {
    dispatch(setCrossClientLandingDecompositionResult(null))
    setReviewState(null)
  }, [dispatch])

  const discardExecution = useCallback(() => {
    const executionId = decompositionResult?.executionId
    if (executionId) {
      triggerDeleteCrossClientExecution({ accountId, executionId })
        .unwrap()
        .catch((e) => console.error(e))
    }
    resetExecution()
  }, [accountId, decompositionResult?.executionId, triggerDeleteCrossClientExecution, resetExecution])

  return {
    initError,
    reviewState,
    decomposeCrossClientQuery,
    finalizeExecution,
    resetExecution,
    discardExecution,
  }
}
